import { useState } from 'react'
import { SparkleIcon, SlidersHorizontalIcon } from '@phosphor-icons/react'
import { ScoopDialog, ScoopButton } from '../../../../ui'
import styles from './SetupChoice.module.scss'

/**
 * Setup choice panel for connector configuration
 * Allows users to choose between AI-guided or manual configuration
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {string} props.connectorName - Display name of the connector (e.g., "HubSpot")
 * @param {Function} props.onSelectAI - Callback when user chooses AI-guided setup
 * @param {Function} props.onSelectManual - Callback when user chooses manual setup
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onBack - Callback when user clicks back
 */
export const SetupChoice = ({
    open,
    connectorName,
    onSelectAI,
    onSelectManual,
    onClose,
    onBack,
}) => {
    const [selectedOption, setSelectedOption] = useState(null)

    const handleNext = () => {
        if (selectedOption === 'ai') {
            onSelectAI()
        } else if (selectedOption === 'manual') {
            onSelectManual()
        }
    }

    return (
        <ScoopDialog
            open={open}
            onClose={onClose}
            title={`Dataset from ${connectorName}`}
            actions={
                <>
                    <ScoopButton variant="text" onClick={onBack}>
                        Back
                    </ScoopButton>
                    <ScoopButton
                        onClick={handleNext}
                        disabled={!selectedOption}
                    >
                        Next
                    </ScoopButton>
                </>
            }
            maxWidth="md"
        >
            <div className={styles.container}>
                <p className={styles.subtitle}>How would you like to configure your dataset?</p>

                <div className={styles.options}>
                    <button
                        className={`${styles.optionCard} ${selectedOption === 'ai' ? styles.selected : ''}`}
                        onClick={() => setSelectedOption('ai')}
                    >
                        <div className={styles.iconContainer}>
                            <SparkleIcon size={32} weight="duotone" />
                        </div>
                        <div className={styles.optionContent}>
                            <h3 className={styles.optionTitle}>Guide me with AI</h3>
                            <p className={styles.optionDescription}>
                                Choose from common presets or describe what you need in plain
                                language. Perfect for getting started quickly.
                            </p>
                        </div>
                    </button>

                    <button
                        className={`${styles.optionCard} ${selectedOption === 'manual' ? styles.selected : ''}`}
                        onClick={() => setSelectedOption('manual')}
                    >
                        <div className={styles.iconContainer}>
                            <SlidersHorizontalIcon size={32} weight="duotone" />
                        </div>
                        <div className={styles.optionContent}>
                            <h3 className={styles.optionTitle}>Configure manually</h3>
                            <p className={styles.optionDescription}>
                                Full control over objects, fields, and filters. Best for advanced
                                users or specific requirements.
                            </p>
                        </div>
                    </button>
                </div>
            </div>
        </ScoopDialog>
    )
}
